import { CategoryDetailsPageContext } from "@NA/Overrides/Pages/CategoryDetailsPage";
import ADIColors from "@NA/Utils/ADIColors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import addProductFilters from "@insite/client-framework/Store/Pages/ProductList/Handlers/AddProductFilters";
import { getProductListDataViewProperty } from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ProductListPageContext } from "@insite/content-library/Pages/ProductListPage";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    pagination: getProductListDataViewProperty(state, "pagination"),
    isSearchPage: state.pages.productList.isSearchPage,
});

const mapDispatchToProps = {
    addProductFilters,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface ProductListSortSelectStyles {
    select?: SelectPresentationProps;
    sortbyStyles?: InjectableCss;
}

export const sortSelectStyles: ProductListSortSelectStyles = {
    select: {
        labelProps: {
            css: css`
                width: unset;
            `,
        },
        cssOverrides: {
            formField: css`
                width: 325px;
            `,
        },
    },
    sortbyStyles: {
        css: css`
            display: inline-flex;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    css`
                        margin-left: 15px;
                    `,
                    null,
                    null,
                ])}
            > label {
                width: 76px;
                padding-right: 11px;
                display: inline-flex;
                align-items: center;
                color: ${ADIColors.darkBlack3};
                font-size: 14px;
                font-weight: 800;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            // For ADINA-57705 Sort by alignment
                            // width: 62px;
                            width: 53px;
                            padding-right: 0px;
                        `,
                        css`
                            width: 62px;
                        `,
                        null,
                        null,
                        null,
                    ])}
            }
            > div[class^="FormFieldStyle"] {
                width: 185px;
                margin-right: 20px;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            width: auto;
                        `,
                        css`
                            width: auto;
                        `,
                        null,
                        null,
                        null,
                    ])}
                > div:first-of-type {
                    width: 0 !important;
                }
                > div[class^="FormInputWrapper"] {
                    > select {
                        width: 164px;
                        ${({ theme }: { theme: BaseTheme }) =>
                            breakpointMediaQueries(theme, [
                                css`
                                    width: 112px !important;
                                    padding-right: 10px !important;
                                    padding-left: 10px !important;
                                `,
                                css`
                                    width: 112px !important;
                                    padding-right: 10px !important;
                                    padding-left: 10px !important;
                                `,
                                null,
                                null,
                                null,
                            ])}
                    }
                    > span {
                        left: 120px;
                        ${({ theme }: { theme: BaseTheme }) =>
                            breakpointMediaQueries(theme, [
                                css`
                                    left: 82px;
                                `,
                                css`
                                    left: 82px;
                                `,
                                null,
                                null,
                                null,
                            ])}
                    }
                }
            }
        `,
    },
};

const styles = sortSelectStyles;

const ProductListSortSelect = ({ addProductFilters, pagination, isSearchPage }: Props) => { 
    let sortOptionList = pagination?.sortOptions;
    const [tempSearchPage, setTempSearchPage] = useState<Boolean>(isSearchPage ? true : false);

if (!pagination) {
        return null;
    }

    const onChangeSortHandler = (event: React.ChangeEvent<HTMLSelectElement>) => {
        setTempSearchPage(false);
        addProductFilters({ sort: event.currentTarget.value });
    };

    if (!isSearchPage) {
        sortOptionList = sortOptionList?.filter(x => x.sortType !== "1"); // to remove `Best Match` option 
    }  

   return (
        <StyledWrapper {...styles.sortbyStyles}>
            <label htmlFor="pagerSortTypeSelector">Sort by</label>
           <Select
               {...styles.select}
               value={tempSearchPage ? "1" : pagination.sortType}
               labelPosition="left"
               id="pagerSortTypeSelector"
               onChange={onChangeSortHandler}
               data-test-selector="productListSortSelect"
           >
               {sortOptionList?.map(sortOption => (
                   <option key={sortOption.sortType} value={sortOption.sortType}>
                       {sortOption.displayName}
                   </option>
               ))}
           </Select>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ProductListSortSelect),
    definition: {
        group: "Product List",
        displayName: "Sort Select",
        allowedContexts: [ProductListPageContext, CategoryDetailsPageContext],
    },
};

export default widgetModule;
